import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Check, X, Trash2, Pencil, Plus, RotateCcw, AlertTriangle, ArchiveRestore } from "lucide-react";

const STATUS = {
  checked:  { label: "OK",      icon: Check, cls: "bg-emerald-500 text-white" },
  missing:  { label: "Mangler", icon: X,     cls: "bg-red-500 text-white" },
  na:       { label: "—",       icon: null,  cls: "bg-slate-300 text-slate-700" },
};

function StatusButton({ active, onClick, kind, testId }) {
  const meta = STATUS[kind];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-9 px-3 rounded-md text-xs font-semibold border transition-colors ${
        active
          ? meta.cls + " border-transparent"
          : "bg-white text-[#2B3F4D] border-slate-200 hover:border-slate-400"
      }`}
    >
      {Icon ? <Icon className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> : null}
      {meta.label}
    </button>
  );
}

function ItemRow({ boatId, item, isAdmin, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(item.note || "");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    label_no: item.label_no, quantity: String(item.quantity), category: item.category,
  });

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const r = await api.patch(
        `/boats/${boatId}/inventory/items/${encodeURIComponent(item.key)}`,
        { status, note: note },
      );
      if (r.data.defect_created) toast.success("Avvik opprettet for manglende inventar");
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere");
    } finally { setBusy(false); }
  };

  const saveNote = async () => {
    try {
      await api.patch(
        `/boats/${boatId}/inventory/items/${encodeURIComponent(item.key)}`,
        { status: item.status, note },
      );
      toast.success("Notat lagret");
      setNoteOpen(false);
      onUpdated();
    } catch { toast.error("Kunne ikke lagre notat"); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(
        `/boats/${boatId}/inventory/items/${encodeURIComponent(item.key)}/edit`,
        {
          label_no: editForm.label_no,
          quantity: parseInt(editForm.quantity, 10) || 1,
          category: editForm.category,
        },
      );
      toast.success("Oppdatert");
      setEditOpen(false);
      onUpdated();
    } catch { toast.error("Kunne ikke lagre"); }
  };

  const remove = async () => {
    const archived = item.category === "not_in_boat";
    const msg = archived
      ? `Permanent slette «${item.label_no}»? Dette kan ikke angres.`
      : `Flytte «${item.label_no}» til "Ikke i båten"? Du kan legge det tilbake senere.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(
        `/boats/${boatId}/inventory/items/${encodeURIComponent(item.key)}`,
      );
      toast.success(archived ? "Slettet permanent" : "Flyttet til «Ikke i båten»");
      onUpdated();
    } catch { toast.error("Kunne ikke fjerne"); }
  };

  const restore = async () => {
    try {
      const r = await api.post(
        `/boats/${boatId}/inventory/items/${encodeURIComponent(item.key)}/restore`,
      );
      toast.success("Lagt tilbake i båten");
      onUpdated();
      return r;
    } catch { toast.error("Kunne ikke legge tilbake"); }
  };

  const archived = item.category === "not_in_boat";
  const status = item.status; // checked | missing | na | undefined
  if (archived) {
    return (
      <div
        data-testid={`inv-row-${item.key}`}
        className="grid grid-cols-[1fr_auto] gap-3 items-center py-3 px-3 border-b border-slate-100 last:border-0 bg-slate-50"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-[#2B3F4D] truncate line-through opacity-80">
              {item.label_no}
            </span>
            <span className="text-xs text-slate-500 font-mono">×{item.quantity}</span>
            {item.custom && <Badge className="bg-[#BD984D]/15 text-[#A57F39] text-[10px] border-0">Egendefinert</Badge>}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Arkivert {item.archived_at ? new Date(item.archived_at).toLocaleString("nb-NO") : ""}
            {item.archived_by_name && <> · av {item.archived_by_name}</>}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-1.5">
            <Button size="sm" onClick={restore} data-testid={`inv-${item.key}-restore`}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white h-8">
              <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> Legg i båten
            </Button>
            <Button variant="outline" size="sm" onClick={remove}
                    className="h-8 px-2 border-red-200 text-red-600 hover:bg-red-50"
                    data-testid={`inv-${item.key}-delete`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={`inv-row-${item.key}`}
      className={`grid grid-cols-[1fr_auto] sm:grid-cols-[2fr_auto_auto] gap-3 items-center py-3 px-3 border-b border-slate-100 last:border-0 ${
        status === "missing" ? "bg-red-50/40" : status === "checked" ? "bg-emerald-50/30" : ""
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-[#0C1824] truncate">{item.label_no}</span>
          <span className="text-xs text-[#A57F39] font-mono">×{item.quantity}</span>
          {item.custom && <Badge className="bg-[#BD984D]/15 text-[#A57F39] text-[10px] border-0">Egendefinert</Badge>}
        </div>
        {item.note && (
          <div className="text-xs text-[#2B3F4D] mt-1 italic">«{item.note}»</div>
        )}
        {item.updated_by_name && status && (
          <div className="text-[11px] text-slate-500 mt-0.5">
            Sist {STATUS[status]?.label?.toLowerCase()} av {item.updated_by_name}
          </div>
        )}
      </div>
      <div className="flex gap-1.5 col-span-2 sm:col-span-1 flex-wrap">
        <StatusButton
          kind="checked" active={status === "checked"} testId={`inv-${item.key}-ok`}
          onClick={() => !busy && setStatus("checked")} />
        <StatusButton
          kind="missing" active={status === "missing"} testId={`inv-${item.key}-missing`}
          onClick={() => !busy && setStatus("missing")} />
        <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="h-9"
                    data-testid={`inv-${item.key}-note`}>
              Notat
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Notat for {item.label_no}</DialogTitle>
              <DialogDescription>
                Beskriv tilstand eller hvor mye som mangler.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="F.eks. «Kun 4 stk – mangler 2»"
              rows={4}
              data-testid={`inv-${item.key}-note-text`}
            />
            <DialogFooter>
              <Button onClick={saveNote} className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white"
                      data-testid={`inv-${item.key}-note-save`}>
                Lagre
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {isAdmin && (
        <div className="flex gap-1 col-span-2 sm:col-span-1">
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 px-2"
                      data-testid={`inv-${item.key}-edit`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Rediger element</DialogTitle>
                <DialogDescription>
                  Endre navn eller antall på dette inventar-elementet.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Navn</Label>
                  <Input value={editForm.label_no}
                         onChange={(e) => setEditForm({ ...editForm, label_no: e.target.value })} />
                </div>
                <div>
                  <Label>Antall</Label>
                  <Input type="number" min={1} value={editForm.quantity}
                         onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveEdit} className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
                        data-testid={`inv-${item.key}-edit-save`}>
                  Lagre
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="sm" onClick={remove}
                  className="h-8 px-2 border-red-200 text-red-600 hover:bg-red-50"
                  data-testid={`inv-${item.key}-delete`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function BoatInventory({ boatId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newItem, setNewItem] = useState({ category: "consumables", label_no: "", quantity: "1" });

  const load = async () => {
    const r = await api.get(`/boats/${boatId}/inventory`);
    setData(r.data);
  };
  useEffect(() => { load(); }, [boatId]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const byCat = new Map();
    for (const c of data.categories) byCat.set(c.key, { ...c, items: [] });
    for (const item of data.items) {
      if (!byCat.has(item.category)) {
        byCat.set(item.category, { key: item.category, label_no: item.category, items: [] });
      }
      byCat.get(item.category).items.push(item);
    }
    return Array.from(byCat.values()).filter((c) => c.items.length);
  }, [data]);

  const reset = async () => {
    if (!window.confirm("Tilbakestille alle avhakinger? Dette starter en ny inventarsjekk.")) return;
    await api.post(`/boats/${boatId}/inventory/reset`);
    toast.success("Inventarliste tilbakestilt");
    load();
  };

  const addItem = async () => {
    if (!newItem.label_no.trim()) return toast.error("Skriv inn navn");
    await api.post(`/boats/${boatId}/inventory/items`, {
      category: newItem.category,
      label_no: newItem.label_no.trim(),
      quantity: parseInt(newItem.quantity, 10) || 1,
    });
    toast.success("Lagt til");
    setNewItem({ category: newItem.category, label_no: "", quantity: "1" });
    setAddOpen(false);
    load();
  };

  if (!data) return <Card className="p-6 text-sm text-[#2B3F4D]">Laster inventar…</Card>;

  return (
    <div className="space-y-4" data-testid="inventory-section">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">Totalt</div>
          <div className="text-2xl font-bold text-[#0C1824] mt-1">{data.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-emerald-700">Sjekket OK</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1" data-testid="inv-count-checked">
            {data.counts.checked}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-red-700">Mangler</div>
          <div className="text-2xl font-bold text-red-600 mt-1" data-testid="inv-count-missing">
            {data.counts.missing}
            {data.counts.missing > 0 && (
              <AlertTriangle className="w-4 h-4 inline -mt-1 ml-1.5 text-red-600" />
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-700">Ikke sjekket</div>
          <div className="text-2xl font-bold text-slate-500 mt-1" data-testid="inv-count-pending">
            {data.counts.pending}
          </div>
        </Card>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-[#2B3F4D]">
          {data.last_reset_at ? (
            <>Sist tilbakestilt {new Date(data.last_reset_at).toLocaleString("nb-NO")}
              {data.last_reset_by_name && <> · {data.last_reset_by_name}</>}</>
          ) : (
            "Ingen tilbakestilling registrert ennå"
          )}
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="inv-add-open"
                        className="border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10">
                  <Plus className="w-4 h-4 mr-1.5" /> Legg til element
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nytt inventar-element</DialogTitle>
                  <DialogDescription>
                    Legges til på denne båtens inventarliste. Andre båter beholder sin
                    eksisterende liste.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Kategori</Label>
                    <Select value={newItem.category}
                            onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                      <SelectTrigger data-testid="inv-add-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {data.categories.filter((c) => c.key !== "not_in_boat").map((c) => (
                          <SelectItem key={c.key} value={c.key}>{c.label_no}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Navn</Label>
                    <Input value={newItem.label_no}
                           data-testid="inv-add-label"
                           onChange={(e) => setNewItem({ ...newItem, label_no: e.target.value })}
                           placeholder="F.eks. Grillklype" />
                  </div>
                  <div>
                    <Label>Antall</Label>
                    <Input type="number" min={1} value={newItem.quantity}
                           data-testid="inv-add-qty"
                           onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={addItem} data-testid="inv-add-save"
                          className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
                    Legg til
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Button variant="outline" onClick={reset} data-testid="inv-reset"
                  className="border-slate-300 text-[#0C1824] hover:bg-slate-50">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Tilbakestill
          </Button>
        </div>
      </div>

      {grouped.map((cat) => {
        const isArchive = cat.key === "not_in_boat";
        return (
          <Card key={cat.key} className="overflow-hidden" data-testid={`inv-cat-${cat.key}`}>
            <div className={`px-4 py-3 text-xs uppercase tracking-[0.22em] ${
              isArchive ? "bg-slate-600 text-white" : "bg-[#0C1824] text-white"
            }`}>
              {cat.label_no}
              <span className={`ml-2 font-mono ${isArchive ? "text-slate-200" : "text-[#ECC974]"}`}>
                {cat.items.length}
              </span>
              {isArchive && (
                <span className="ml-3 text-[10px] tracking-normal lowercase text-slate-200 normal-case">
                  Tatt ut av båten – kan legges tilbake av admin
                </span>
              )}
            </div>
            <div>
              {cat.items.map((it) => (
                <ItemRow key={it.key} boatId={boatId} item={it} isAdmin={isAdmin}
                         onUpdated={load} />
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
